import { create } from 'zustand';

/**
 * The transient half of this console's feedback.
 *
 * WHY A STORE AND NOT A HOOK. Every write in this app is a `useMutation`, and
 * its `onSuccess`/`onError` run outside render — a mutation declared at the top
 * of a page cannot call a hook from inside its own callback, and the global
 * `MutationCache` in `mutation-feedback.ts` has no component around it at all.
 * So the queue is a module-level store and the entry point is a plain function.
 * `Toaster` is the only thing that subscribes.
 *
 * WHAT BELONGS HERE, AND WHAT DOES NOT. A toast is for the confirmation that a
 * write landed, and for a failure that has nowhere else to appear. A failure
 * raised by a form still belongs in that form's banner, next to the fields the
 * user is looking at — see `mutation-feedback.ts` for how the two divide.
 */
export type NoticeTone = 'success' | 'error' | 'info';

export interface Notice {
  id: number;
  tone: NoticeTone;
  message: string;
}

/**
 * How many can be on screen at once.
 *
 * Bulk actions are the reason there is a cap: clearing eight fraud flags fires
 * eight mutations, and a column of eight identical toasts buries whatever the
 * ninth one says. The oldest are dropped, because the newest is the one the
 * user just caused.
 */
const MAX_NOTICES = 3;

interface NoticeStore {
  notices: Notice[];
  dismiss: (id: number) => void;
}

let nextId = 0;

export const useNotices = create<NoticeStore>((set) => ({
  notices: [],
  dismiss: (id) => set((state) => ({ notices: state.notices.filter((notice) => notice.id !== id) })),
}));

function push(tone: NoticeTone, message: string): void {
  const text = message.trim();
  // An empty toast is worse than none: it is a box with an icon and no sentence.
  // `apiErrorMessage` returns '' for a nullish error, so this is reachable.
  if (text === '') return;

  useNotices.setState((state) => {
    /*
     * A repeat of what is already showing REPLACES it rather than stacking.
     * Double-clicking Simpan, or a save that fails the same way twice, should
     * read as one event — and re-adding it with a fresh id restarts the timer,
     * which is the behaviour a second click is asking for.
     */
    const rest = state.notices.filter((notice) => !(notice.tone === tone && notice.message === text));
    nextId += 1;
    return { notices: [...rest, { id: nextId, tone, message: text }].slice(-MAX_NOTICES) };
  });
}

export const notify = {
  success: (message: string) => push('success', message),
  error: (message: string) => push('error', message),
  info: (message: string) => push('info', message),
};
